import { useState, useEffect } from "react";
import { Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection, { inputClass } from "./ConfigSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const DAYS = [
  { key: "seg", label: "Segunda-feira" },
  { key: "ter", label: "Terça-feira" },
  { key: "qua", label: "Quarta-feira" },
  { key: "qui", label: "Quinta-feira" },
  { key: "sex", label: "Sexta-feira" },
  { key: "sab", label: "Sábado" },
  { key: "dom", label: "Domingo" },
];

interface DaySchedule {
  is_open: boolean;
  is_24h: boolean;
  opening_time: string;
  closing_time: string;
}

type Schedules = Record<string, DaySchedule>;

const defaultSchedule: DaySchedule = {
  is_open: true,
  is_24h: false,
  opening_time: "08:00",
  closing_time: "22:00",
};

const Horario = () => {
  const { form, update, restaurant } = useConfig();
  const [schedules, setSchedules] = useState<Schedules>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load schedules from DB
  useEffect(() => {
    if (!restaurant) return;
    const load = async () => {
      const { data } = await supabase
        .from("restaurant_schedules" as any)
        .select("*")
        .eq("restaurant_id", restaurant.id);

      const rows = (data || []) as any[];
      const map: Schedules = {};
      DAYS.forEach((d) => {
        const found = rows.find((r: any) => r.day_of_week === d.key);
        if (found) {
          map[d.key] = {
            is_open: found.is_open,
            is_24h: found.is_24h,
            opening_time: found.opening_time?.slice(0, 5) || "08:00",
            closing_time: found.closing_time?.slice(0, 5) || "22:00",
          };
        } else {
          // Fallback: use legacy opening_time/closing_time + operating_days
          const legacyDays = Array.isArray((restaurant as any).operating_days)
            ? (restaurant as any).operating_days as string[]
            : ["seg", "ter", "qua", "qui", "sex", "sab"];
          map[d.key] = {
            is_open: legacyDays.includes(d.key),
            is_24h: !!(restaurant as any).is_24h,
            opening_time: restaurant.opening_time?.slice(0, 5) || "08:00",
            closing_time: restaurant.closing_time?.slice(0, 5) || "22:00",
          };
        }
      });
      setSchedules(map);
      setLoading(false);
    };
    load();
  }, [restaurant]);

  const updateDay = (day: string, field: keyof DaySchedule, value: any) => {
    setSchedules((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const applyToAll = (sourceDay: string) => {
    const source = schedules[sourceDay];
    if (!source) return;
    setSchedules((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (d.key !== sourceDay) {
          next[d.key] = { ...source };
        }
      });
      return next;
    });
    toast.success("Horário aplicado a todos os dias!");
  };

  const saveSchedules = async () => {
    if (!restaurant) return;
    setSaving(true);
    try {
      // Upsert all schedules
      const rows = DAYS.map((d) => ({
        restaurant_id: restaurant.id,
        day_of_week: d.key,
        is_open: schedules[d.key]?.is_open ?? true,
        is_24h: schedules[d.key]?.is_24h ?? false,
        opening_time: schedules[d.key]?.opening_time || "08:00",
        closing_time: schedules[d.key]?.closing_time || "22:00",
      }));

      const { error } = await supabase
        .from("restaurant_schedules" as any)
        .upsert(rows, { onConflict: "restaurant_id,day_of_week" });

      if (error) throw error;

      // Also sync legacy fields for backward compatibility
      const openDays = DAYS.filter((d) => schedules[d.key]?.is_open).map((d) => d.key);
      update("operating_days", openDays);

      toast.success("Horários salvos com sucesso!");
    } catch (err: any) {
      toast.error("Erro ao salvar horários: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ConfigSection title="Horário de Funcionamento" icon={Clock}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ConfigSection>
    );
  }

  return (
    <ConfigSection title="Horário de Funcionamento" icon={Clock}>
      {/* Manual close toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/10 border border-destructive/20">
        <div>
          <p className="text-sm font-semibold text-destructive">Fechar loja manualmente</p>
          <p className="text-xs text-muted-foreground mt-0.5">Impede novos pedidos no cardápio digital</p>
        </div>
        <Switch
          checked={form.is_manually_closed}
          onCheckedChange={(checked) => update("is_manually_closed", checked)}
        />
      </div>

      {/* Per-day schedules */}
      <div className="space-y-3">
        {DAYS.map((day, idx) => {
          const sched = schedules[day.key] || defaultSchedule;
          return (
            <div
              key={day.key}
              className={`rounded-xl border p-4 transition-colors ${
                sched.is_open
                  ? "bg-card border-border"
                  : "bg-muted/30 border-border/50 opacity-70"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={sched.is_open}
                    onCheckedChange={(checked) => updateDay(day.key, "is_open", checked)}
                  />
                  <span className="text-sm font-semibold">{day.label}</span>
                </div>
                {sched.is_open && idx === 0 && (
                  <button
                    type="button"
                    onClick={() => applyToAll(day.key)}
                    className="text-[11px] text-primary hover:underline font-medium"
                  >
                    Aplicar a todos
                  </button>
                )}
              </div>

              {sched.is_open && (
                <div className="space-y-3 pl-10">
                  {/* 24h toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sched.is_24h}
                      onCheckedChange={(checked) => updateDay(day.key, "is_24h", checked)}
                    />
                    <span className="text-xs text-muted-foreground">Aberto 24 horas</span>
                  </div>

                  {/* Time inputs (hidden if 24h) */}
                  {!sched.is_24h && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Abertura</Label>
                        <Input
                          type="time"
                          value={sched.opening_time}
                          onChange={(e) => updateDay(day.key, "opening_time", e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Fechamento</Label>
                        <Input
                          type="time"
                          value={sched.closing_time}
                          onChange={(e) => updateDay(day.key, "closing_time", e.target.value)}
                          className={inputClass}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save button */}
      <button
        type="button"
        onClick={saveSchedules}
        disabled={saving}
        className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        Salvar Horários
      </button>
    </ConfigSection>
  );
};

export default Horario;
